import { Mail, ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { authService } from "@/integrations/auth/authService";
import { useTranslation, Trans } from "react-i18next";

export const EmailSection = () => {
  const { t } = useTranslation();
  const { user, updateEmail, updatePassword, refreshSession } = useAuth();
  const { addNotification } = useNotifications();

  const [isExpanded, setIsExpanded] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [isEmailSaving, setIsEmailSaving] = useState(false);
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);

  // Новые состояния для подключения email
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);

  // Проверяем, заканчивается ли email на @telegram-user.com
  const isEmailNotConnected =
    user?.email?.endsWith("@telegram-user.com") || false;

  const handleEmailConnect = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация
    if (newPassword !== confirmPassword) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.passwords_mismatch'),
      });
      return;
    }

    if (newPassword.length < 6) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.password_too_short'),
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.invalid_email'),
      });
      return;
    }

    setIsConnecting(true);

    try {
      await authService.setEmailPassword(newEmail, newPassword);

      addNotification({
        userId: user?.id || "",
        type: "success",
        message: t('profile.email_section.connect_success'),
      });

      // Обновляем сессию для получения новых данных
      await refreshSession();

      // Сбрасываем форму
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
      setShowConnectForm(false);
    } catch (error) {
      console.error("Ошибка при подключении email:", error);
      addNotification({
        userId: user?.id || "",
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : t('profile.email_section.connect_error_default'),
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (email === user?.email) {
      addNotification({
        userId: user?.id || "",
        type: "info",
        message: t('profile.email_section.same_email'),
      });
      return;
    }

    // Валидация email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.invalid_email'),
      });
      return;
    }

    setIsEmailSaving(true);

    try {
      const { error } = await updateEmail(email);

      if (error) {
        if (
          error.message.includes("Сессия истекла") ||
          error.message.includes("войдите снова") ||
          error.message.includes("session_not_found")
        ) {
          addNotification({
            userId: user?.id || "",
            type: "error",
            message: t('profile.email_section.session_expired'),
          });
        } else {
          addNotification({
            userId: user?.id || "",
            type: "error",
            message: error.message || t('profile.email_section.update_email_error_default'),
          });
        }
      } else {
        addNotification({
          userId: user?.id || "",
          type: "success",
          message: t('profile.email_section.update_email_sent'),
        });
      }
    } catch (error) {
      console.error("Ошибка при обновлении email:", error);
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.update_email_error_default'),
      });
    } finally {
      setIsEmailSaving(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const formElement = e.target as HTMLFormElement;
    const newPasswordInput = formElement.querySelector(
      "#new-password"
    ) as HTMLInputElement;
    const confirmPasswordInput = formElement.querySelector(
      "#confirm-password"
    ) as HTMLInputElement;
    const newPassword = newPasswordInput.value;
    const confirmPassword = confirmPasswordInput.value;

    if (newPassword !== confirmPassword) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.passwords_mismatch'),
      });
      return;
    }

    if (newPassword.length < 6) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.password_too_short'),
      });
      return;
    }

    setIsPasswordSaving(true);

    try {
      const { error } = await updatePassword(newPassword);

      if (error) {
        if (
          error.message.includes("Сессия истекла") ||
          error.message.includes("войдите снова") ||
          error.message.includes("session_not_found")
        ) {
          addNotification({
            userId: user?.id || "",
            type: "error",
            message: t('profile.email_section.session_expired'),
          });
        } else {
          addNotification({
            userId: user?.id || "",
            type: "error",
            message: error.message || t('profile.email_section.update_password_error_default'),
          });
        }
      } else {
        newPasswordInput.value = "";
        confirmPasswordInput.value = "";
        addNotification({
          userId: user?.id || "",
          type: "success",
          message: t('profile.email_section.password_updated'),
        });
      }
    } catch (error) {
      console.error("Ошибка при обновлении пароля:", error);
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.email_section.update_password_error_default'),
      });
    } finally {
      setIsPasswordSaving(false);
    }
  };

  return (
    <div className="bg-card border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-brand-100 text-brand-500 rounded-full flex items-center justify-center">
            <Mail size={20} />
          </div>
          <div>
            <h2 className="text-xl font-medium">{t('profile.email_section.title')}</h2>
            {!isExpanded && !showConnectForm && (
              <p className="text-sm text-muted-foreground mt-1">
                {isEmailNotConnected
                  ? t('profile.email_section.not_connected')
                  : user?.email || t('profile.email_section.not_specified')}
              </p>
            )}
          </div>
        </div>

        {isEmailNotConnected && !showConnectForm ? (
          <Button
            onClick={() => setShowConnectForm(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            {t('profile.email_section.connect_btn')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-10 w-10"
          >
            <ChevronDown
              size={20}
              className={`transition-transform ${isExpanded ? "rotate-180" : ""
                }`}
            />
          </Button>
        )}
      </div>

      {/* Форма подключения email для Telegram пользователей */}
      {isEmailNotConnected && showConnectForm && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>{t('profile.email_section.connect_form_title')}</strong>
              <br />
              {t('profile.email_section.connect_form_desc')}
            </p>
          </div>

          <form onSubmit={handleEmailConnect} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="connect-email">{t('profile.email_section.email_label')}</Label>
              <Input
                id="connect-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="connect-password">{t('profile.email_section.password_label')}</Label>
              <Input
                id="connect-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('profile.password.new_placeholder')}
                minLength={6}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="connect-confirm-password">
                {t('profile.email_section.confirm_password_label')}
              </Label>
              <Input
                id="connect-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('profile.password.confirm_label')}
                minLength={6}
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowConnectForm(false);
                  setNewEmail("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                disabled={isConnecting}
              >
                {t('profile.email_section.cancel_btn')}
              </Button>
              <Button type="submit" disabled={isConnecting}>
                {isConnecting ? t('profile.email_section.connecting_btn') : t('profile.email_section.connect_btn')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Существующий интерфейс для пользователей с подключенным email */}
      {!isEmailNotConnected && isExpanded && (
        <div className="space-y-6">
          <form onSubmit={handleEmailUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('profile.email_section.email_label')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                💡 <strong>{t('profile.email_section.important_note')}</strong> <Trans i18nKey="profile.email_section.change_email_note" components={{ strong: <strong /> }} />
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isEmailSaving}>
                {isEmailSaving ? t('profile.email_section.saving_btn') : t('profile.email_section.save_email_btn')}
              </Button>
            </div>
          </form>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">{t('profile.email_section.change_password_title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('profile.email_section.change_password_desc')}
            </p>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('profile.email_section.login_password_label')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  required
                  minLength={6}
                  placeholder={t('profile.password.new_placeholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">
                  {t('profile.email_section.confirm_new_password_label')}
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={isPasswordSaving}>
                  {isPasswordSaving ? t('profile.email_section.saving_btn') : t('profile.email_section.save_password_btn')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
